/**
 * Prompts in the widget bar (#1825). A click puts a prompt into the chat and
 * never sends it, because a prompt usually still has placeholders to fill.
 */

import { useTranslation } from 'react-i18next'
import { ArrowRight, BookOpen, Copy, Maximize2 } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { logError } from '@/lib/logger'
import { useWidgetArea } from '@/contexts/WidgetAreaContext'
import type { WidgetProps } from '@/types/widget'
import { WIDGET_TILE_CLASS } from './tile'
import { usePromptList } from './usePromptList'
import { useSendPromptToChat } from './useSendPromptToChat'

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-interactive-color)]'

export function PromptsBarWidget({ config, staticPreview }: WidgetProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { isPromptsPanelOpen, openPromptsPanel, closePromptsPanel } = useWidgetArea()
  const { prompts, loading, failed, reload } = usePromptList(!staticPreview)
  const sendToChat = useSendPromptToChat()
  const isSmall = config.size === 'small'
  const label = t('widgetBar.names.prompt-library')

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast({ title: t('common.success'), description: t('common.copied') })
    } catch (error) {
      logError(error instanceof Error ? error : new Error(String(error)), { context: 'PromptsBarWidget.copy' })
      toast({ title: t('common.error'), description: t('common.copyFailed'), variant: 'destructive' })
    }
  }

  const expand = (
    <button
      type="button"
      data-prompts-expand
      aria-label={t(isPromptsPanelOpen ? 'widgetBar.prompts.collapse' : 'widgetBar.prompts.expand')}
      aria-expanded={isPromptsPanelOpen}
      onClick={() => (isPromptsPanelOpen ? closePromptsPanel() : openPromptsPanel())}
      className={`flex h-11 w-11 flex-none items-center justify-center rounded-full text-[var(--text-color)] ${FOCUS_RING}`}
    >
      <Maximize2 className="h-[18px] w-[18px]" aria-hidden />
    </button>
  )

  const header = (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-xs font-medium text-[var(--text-secondary)]">
        <BookOpen className="h-4 w-4" aria-hidden />
        {label}
      </span>
      {expand}
    </div>
  )

  const tileClass = `${WIDGET_TILE_CLASS} flex-col gap-1 pb-3 pl-4 pr-3 pt-2.5 ${
    isPromptsPanelOpen ? 'border-[var(--theme-interactive-color)]' : ''
  }`

  if (isSmall) {
    return (
      <section aria-label={label} className={tileClass}>
        {header}
        <div className="mt-auto">
          <div className="text-[32px] font-black leading-none tabular-nums">{loading ? '–' : prompts.length}</div>
          <div className="mt-1 text-xs text-[var(--text-secondary)]">{t('widgetBar.prompts.count', { count: prompts.length })}</div>
        </div>
      </section>
    )
  }

  return (
    <section aria-label={label} className={`${tileClass} min-h-[124px]`}>
      {header}
      {failed ? (
        <div className="flex flex-col items-start gap-2 pb-1">
          <span className="text-[13px] text-[var(--text-secondary)]">{t('widgetBar.prompts.loadFailed')}</span>
          <button
            type="button"
            onClick={() => void reload()}
            className={`h-9 rounded-full border border-[var(--window-border-color)] px-3.5 text-[13px] ${FOCUS_RING}`}
          >
            {t('widgetBar.prompts.retry')}
          </button>
        </div>
      ) : !loading && prompts.length === 0 ? (
        <div className="flex flex-col items-start gap-2 pb-1">
          <span className="text-[13px] text-[var(--text-secondary)]">{t('widgetBar.prompts.empty')}</span>
          <button
            type="button"
            onClick={() => openPromptsPanel()}
            className={`h-9 rounded-full border border-[var(--window-border-color)] px-3.5 text-[13px] ${FOCUS_RING}`}
          >
            {t('widgetBar.prompts.new')}
          </button>
        </div>
      ) : (
        prompts.slice(0, 3).map((prompt) => (
          <div
            key={prompt.id}
            className="flex h-11 flex-none items-center rounded-[10px] border border-[var(--window-border-color)] text-sm text-[var(--text-color)]"
          >
            <button
              type="button"
              title={t('widgetBar.prompts.toChat')}
              onClick={() => sendToChat(prompt.prompt_text)}
              className={`flex h-full min-w-0 flex-1 items-center justify-between gap-2 rounded-[10px] pl-2.5 pr-1.5 text-left ${FOCUS_RING}`}
            >
              <span className="min-w-0 truncate">{prompt.title}</span>
              <ArrowRight className="h-4 w-4 flex-none" aria-hidden />
            </button>
            <button
              type="button"
              aria-label={t('widgetBar.prompts.copy', { title: prompt.title })}
              title={t('common.copy')}
              onClick={() => void copy(prompt.prompt_text)}
              className={`flex h-full w-9 flex-none items-center justify-center rounded-[10px] ${FOCUS_RING}`}
            >
              <Copy className="h-4 w-4" aria-hidden />
            </button>
          </div>
        ))
      )}
    </section>
  )
}
